import { useNavigate } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import { useI18n } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Frenchies+Fitness+Coronado+Padel+Center+Nueva+Gorgona+Panama";
const EMBED_URL = "https://www.google.com/maps?q=Coronado+Padel+Center,+Nueva+Gorgona,+Panam%C3%A1&output=embed";

export default function Horarios() {
  const { tl } = useI18n();
  const navigate = useNavigate();
  const HOURS = [
    { d: { es: "Abierto todos los días", en: "Open every day", fr: "Ouvert tous les jours" }, h: "07:00 – 19:00" },
  ];
  const GROUP = [
    { d: { es: "Lunes – Viernes", en: "Monday – Friday", fr: "Lundi – Vendredi" }, h: "07:00 · 08:30 · 18:00" },
    { d: { es: "Sábado", en: "Saturday", fr: "Samedi" }, h: "09:00" },
  ];
  return (
    <PublicLayout>
      <Seo
        title="Horarios y ubicación — Frenchies Fitness & Pilates Coronado"
        description="Horarios de clases de fitness, y Pilates en Coronado, Panamá. Cómo llegar: Coronado Padel Center, Nueva Gorgona (Panamá Oeste). Reserva en línea."
        path="/horarios"
        keywords="horarios gym Coronado, horario horario Pilates Coronado, cómo llegar Frenchies Fitness, Coronado Padel Center, Nueva Gorgona gym"
      />
      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
          {tl({ es: "Horarios y ubicación en Coronado", en: "Schedule & location in Coronado", fr: "Horaires et adresse à Coronado" })}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {tl({ es: "Encuentra el horario de clases de fitness, y Pilates de Frenchies y cómo llegar a nuestro estudio en el Coronado Padel Center.",
                en: "Find Frenchies' fitness and Pilates class schedule and how to reach our studio at the Coronado Padel Center.",
                fr: "Retrouve le planning des cours de fitness et Pilates de Frenchies et l'itinéraire vers notre studio au Coronado Padel Center." })}
        </p>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div data-testid="horarios-info" className="space-y-5 rounded-2xl border border-border bg-card p-6">
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="font-semibold">Frenchies Fitness & Pilates</div>
                <div className="text-sm text-muted-foreground">Coronado Padel Center, Nueva Gorgona<br />Coronado, Panamá Oeste, Panamá</div>
                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="horarios-directions" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                  <Navigation className="h-4 w-4" />{tl({ es: "Cómo llegar (Google Maps)", en: "Directions (Google Maps)", fr: "Itinéraire (Google Maps)" })}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <a href="https://wa.me/50768004547" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">+507 6800-4547 (WhatsApp)</a>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="text-sm">
                <div className="font-semibold">{tl({ es: "Horario del estudio", en: "Studio hours", fr: "Horaires du studio" })}</div>
                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                  {HOURS.map((r, i) => <li key={i} className="flex justify-between gap-4"><span>{tl(r.d)}</span><span>{r.h}</span></li>)}
                </ul>
                <div className="mt-3 font-semibold">{tl({ es: "Clases grupales (1 h)", en: "Group classes (1 h)", fr: "Cours collectifs (1 h)" })}</div>
                <ul className="mt-1 space-y-0.5 text-muted-foreground" data-testid="horarios-group">
                  {GROUP.map((r, i) => <li key={i} className="flex justify-between gap-4"><span>{tl(r.d)}</span><span>{r.h}</span></li>)}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  {tl({ es: "Clases privadas y Pilates: disponibles en todo el horario de apertura, con reserva.", en: "Private classes & Pilates: available across opening hours, by booking.", fr: "Cours privés & Pilates : disponibles sur toute l'amplitude d'ouverture, sur réservation." })}
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border lg:col-span-2">
            <iframe title="Frenchies Fitness Coronado map" src={EMBED_URL} loading="lazy" className="h-72 w-full lg:h-full" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>

        <h2 className="mb-6 mt-14 font-display text-2xl font-bold tracking-tight md:text-3xl">{tl({ es: "Horario de clases", en: "Class schedule", fr: "Planning des cours" })}</h2>
        <ScheduleCalendar onBook={() => navigate("/login")} onWaitlist={() => navigate("/login")} />
      </section>
    </PublicLayout>
  );
}
